/**
 * QMS Repository
 * Data access layer for Quality & Risk Management
 * Implements Clean Architecture repository pattern
 */

import { httpCoreService } from "@/services/HttpCoreService";
import type { ApiResponse } from "@/types/auth.type";
import type {
  QMSRecord,
  ApiQMSRecord,
  CreateQMSRecordRequest,
  UpdateQMSRecordRequest,
  QMSListParams,
  QMSListResponse,
  QMSDashboardData,
  QMSComment,
  CAPARecord,
} from "@/types/qms.type";

// ─── Helper Functions ────────────────────────────────────────────────────────

function assertSuccess<T>(
  response: ApiResponse<T>,
  errorMsg: string,
): asserts response is ApiResponse<T> & { data: T } {
  const ok = response.statusCode >= 200 && response.statusCode < 300;
  if (!ok) {
    throw new Error(response.error ?? response.message ?? errorMsg);
  }
  if (!response.data) {
    throw new Error(`${errorMsg}: No data returned from server.`);
  }
}

/** Normalize comment payloads (API field names can vary slightly). */
function fromApiQMSComment(raw: unknown): QMSComment {
  const c = (raw ?? {}) as Record<string, unknown>;
  const text = String(c.text ?? c.content ?? c.body ?? "");
  const author = String(
    c.author ?? c.authorName ?? c.userName ?? c.user?.toString?.() ?? "Unknown",
  );
  const derivedInitials = author
    .split(/\s+/)
    .filter(Boolean)
    .map((p) => p[0] ?? "")
    .join("")
    .slice(0, 2)
    .toUpperCase();
  const authorInitials = String(
    (c.authorInitials ?? c.initials ?? derivedInitials) || "?",
  );
  return {
    id: String(
      c.id ??
        `comment-${String(c.timestamp ?? c.createdAt ?? c.createdDate ?? "")}-${authorInitials}-${text.trim().slice(0, 32)}`,
    ),
    text,
    author:
      author === "Unknown" && authorInitials !== "?" ? authorInitials : author,
    authorInitials: authorInitials || "?",
    authorRole: String(c.authorRole ?? c.userRole ?? c.role ?? ""),
    timestamp: String(
      c.timestamp ?? c.createdAt ?? c.createdDate ?? new Date().toISOString(),
    ),
  };
}

function extractCommentsArray(api: unknown): unknown[] {
  if (!api || typeof api !== "object") {
    return [];
  }
  const o = api as Record<string, unknown>;
  let firstArray: unknown[] | null = null;
  for (const key of [
    "comments",
    "qrmComments",
    "recordComments",
    "commentList",
    "qrmCommentList",
    "qrm_comment_list",
  ]) {
    const value = o[key];
    if (Array.isArray(value)) {
      if (value.length > 0) {
        return value;
      }
      firstArray ??= value;
      continue;
    }
    if (
      value &&
      typeof value === "object" &&
      Array.isArray((value as { items?: unknown[] }).items)
    ) {
      const items = (value as { items: unknown[] }).items;
      if (items.length > 0) {
        return items;
      }
      firstArray ??= items;
    }
  }
  // Dedicated list endpoint may return the array as `data` already unwrapped,
  // or `{ items: [] }` at the top level.
  if (Array.isArray(o.items)) {
    return o.items;
  }
  if (o.comment && typeof o.comment === "object") {
    return [o.comment];
  }
  return firstArray ?? [];
}

function pickString(
  input: Record<string, unknown>,
  keys: string[],
  fallback = "",
): string {
  for (const key of keys) {
    const value = input[key];
    if (typeof value === "string" && value.trim()) {
      return value;
    }
  }
  return fallback;
}

function pickBoolean(
  input: Record<string, unknown>,
  keys: string[],
  fallback = false,
): boolean {
  for (const key of keys) {
    const value = input[key];
    if (typeof value === "boolean") {
      return value;
    }
  }
  return fallback;
}

function fromApiCapa(raw: unknown): CAPARecord | undefined {
  if (!raw || typeof raw !== "object") {
    return undefined;
  }
  const c = raw as Record<string, unknown>;
  const correctiveAction = pickString(c, [
    "correctiveAction",
    "corrective_action",
  ]);
  const preventiveAction = pickString(c, [
    "preventiveAction",
    "preventive_action",
  ]);
  const capaOwner = pickString(c, ["capaOwner", "capa_owner", "owner"]);
  const capaOwnerInitials = pickString(c, [
    "capaOwnerInitials",
    "capa_owner_initials",
    "ownerInitials",
  ]);
  const capaDueDate = pickString(c, [
    "capaDueDate",
    "capa_due_date",
    "dueDate",
    "due_date",
  ]);
  const effectivenessCheck = pickString(c, [
    "effectivenessCheck",
    "effectiveness_check",
  ]);

  // If the payload doesn't look like a CAPA object, do not synthesize one.
  const hasAnyCoreField =
    correctiveAction || preventiveAction || capaOwner || effectivenessCheck;
  if (!hasAnyCoreField) {
    return undefined;
  }

  const capa: CAPARecord = {
    correctiveAction,
    preventiveAction,
    capaOwner,
    capaOwnerInitials,
    capaDueDate,
    effectivenessCheck,
    effectivenessCheckDate: pickString(c, [
      "effectivenessCheckDate",
      "effectiveness_check_date",
    ]),
    effectivenessApproved: pickBoolean(c, [
      "effectivenessApproved",
      "effectiveness_approved",
      "approved",
    ]),
  };
  const approvedBy = pickString(c, ["approvedBy", "approved_by"]);
  const approvedDate = pickString(c, ["approvedDate", "approved_date"]);
  if (approvedBy) {
    capa.approvedBy = approvedBy;
  }
  if (approvedDate) {
    capa.approvedDate = approvedDate;
  }
  return capa;
}

function extractCapa(api: unknown): CAPARecord | undefined {
  if (!api || typeof api !== "object") {
    return undefined;
  }
  const o = api as Record<string, unknown>;
  const directKeys = [
    "capa",
    "qrmCapa",
    "recordCapa",
    "capaDetails",
    "capaRecord",
    "qrm_capa",
  ];
  for (const key of directKeys) {
    const value = o[key];
    if (Array.isArray(value) && value.length > 0) {
      const parsedFromArray = fromApiCapa(value[0]);
      if (parsedFromArray) {
        return parsedFromArray;
      }
    }
    const parsed = fromApiCapa(value);
    if (parsed) {
      return parsed;
    }
  }
  return undefined;
}

/** CAPA create/update body — omit blank optional dates. */
function toApiCapaPayload(capa: CAPARecord): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    correctiveAction: capa.correctiveAction,
    preventiveAction: capa.preventiveAction,
    capaOwner: capa.capaOwner,
    capaOwnerInitials: capa.capaOwnerInitials,
    capaDueDate: capa.capaDueDate,
    effectivenessCheck: capa.effectivenessCheck,
    effectivenessApproved: capa.effectivenessApproved ?? false,
  };
  if (capa.effectivenessCheckDate?.trim()) {
    payload.effectivenessCheckDate = capa.effectivenessCheckDate;
  }
  return payload;
}

/**
 * Convert API response to frontend QMS record format
 * Maps backend camelCase fields to frontend structure
 */
function fromApiQMSRecord(
  api: ApiQMSRecord,
  studyName?: string,
  siteName?: string,
  ownerName?: string,
  ownerInitials?: string,
  ownerRole?: string,
): QMSRecord {
  return {
    id: api.id,
    recordCode: api.recordCode,
    type: api.type,
    title: api.title,
    description: api.description,
    study: studyName ?? `Study ${api.studyId}`,
    studyId: api.studyId,
    site: siteName ?? (api.siteId ? `Site ${api.siteId}` : "Not assigned"),
    siteId: api.siteId,
    severity: api.severity,
    source: api.source,
    owner: ownerName ?? "Unknown",
    ownerId: api.ownerId,
    ownerInitials: ownerInitials ?? "UK",
    ownerRole: ownerRole ?? "Unknown",
    status: api.status,
    createdDate: api.createdDate,
    dueDate: api.dueDate,
    closedDate: api.closedDate,
    rootCause: api.rootCause,
    investigationNotes: api.investigationNotes,
    investigatedBy: api.investigatedById,
    investigationDate: api.investigationDate,
    capa: extractCapa(api),
    comments: extractCommentsArray(api).map(fromApiQMSComment),
    history: api.history ?? [],
    autoTriggered: api.autoTriggered,
    triggerSource: api.triggerSource,
  };
}

// ─── Repository Interface ────────────────────────────────────────────────────

export interface IQMSRepository {
  // Dashboard
  getDashboard(studyId: string): Promise<QMSDashboardData>;

  // List & Filter
  getQMSRecords(
    studyId: string,
    params?: QMSListParams,
  ): Promise<QMSListResponse>;

  // Single Record
  getQMSRecordById(studyId: string, recordId: string): Promise<QMSRecord>;

  // Create, Update, Delete
  createQMSRecord(
    studyId: string,
    data: CreateQMSRecordRequest,
  ): Promise<QMSRecord>;

  updateQMSRecord(
    studyId: string,
    recordId: string,
    data: UpdateQMSRecordRequest,
  ): Promise<QMSRecord>;

  deleteQMSRecord(studyId: string, recordId: string): Promise<void>;

  // Status Changes
  closeRecord(studyId: string, recordId: string, note?: string): Promise<void>;

  reopenRecord(studyId: string, recordId: string, note?: string): Promise<void>;

  // Comments
  addComment(
    studyId: string,
    recordId: string,
    text: string,
  ): Promise<QMSComment>;
  listComments(studyId: string, recordId: string): Promise<QMSComment[]>;
  updateComment(
    studyId: string,
    recordId: string,
    commentId: string,
    text: string,
  ): Promise<QMSComment>;
  deleteComment(
    studyId: string,
    recordId: string,
    commentId: string,
  ): Promise<void>;

  getComments(studyId: string, recordId: string): Promise<QMSComment[]>;

  // CAPA Management
  addCapa(
    studyId: string,
    recordId: string,
    capa: CAPARecord,
  ): Promise<QMSRecord>;

  updateCapa(
    studyId: string,
    recordId: string,
    capa: CAPARecord,
  ): Promise<QMSRecord>;

  approveCapa(
    studyId: string,
    recordId: string,
    payload: {
      effectivenessNotes?: string;
      eSignature: import("@/types/eSignature.type").ESignatureCeremonyPayload;
    },
  ): Promise<QMSRecord>;
}

// ─── Repository Implementation ───────────────────────────────────────────────

export class QMSRepository implements IQMSRepository {
  /**
   * Get QMS dashboard metrics for a study
   * GET /api/v1/studies/{studyId}/qrm/dashboard
   */
  async getDashboard(studyId: string): Promise<QMSDashboardData> {
    const response = await httpCoreService.get<
      Omit<QMSDashboardData, "recentRecords"> & {
        recentRecords?: ApiQMSRecord[] | QMSRecord[];
      }
    >(`/studies/${studyId}/qrm/dashboard`);
    assertSuccess(response, "Failed to load QMS dashboard");
    const data = response.data;
    const recentRecords = (data.recentRecords ?? []).map((rec) => {
      // Dashboard may return API-shaped or already-mapped records.
      if ("owner" in rec && typeof rec.owner === "string") {
        return rec;
      }
      return fromApiQMSRecord(rec);
    });
    return {
      ...data,
      recentRecords,
    };
  }

  /**
   * Get paginated list of QMS records with filters
   * GET /api/v1/studies/{studyId}/qrm/records
   */
  async getQMSRecords(
    studyId: string,
    params?: QMSListParams,
  ): Promise<QMSListResponse> {
    const response = await httpCoreService.get<{
      items: ApiQMSRecord[];
      total: number;
      page: number;
      limit: number;
    }>(`/studies/${studyId}/qrm/records`, { params });
    assertSuccess(response, "Failed to load QMS records");

    // Convert API records to frontend format
    const items = response.data.items.map((apiRecord) =>
      fromApiQMSRecord(apiRecord),
    );

    return {
      items,
      total: response.data.total,
      page: response.data.page,
      limit: response.data.limit,
    };
  }

  /**
   * Get single QMS record by ID
   * GET /api/v1/studies/{studyId}/qrm/records/{recordId}
   */
  async getQMSRecordById(
    studyId: string,
    recordId: string,
  ): Promise<QMSRecord> {
    const response = await httpCoreService.get<ApiQMSRecord>(
      `/studies/${studyId}/qrm/records/${recordId}`,
    );
    assertSuccess(response, "Failed to load QMS record");
    const record = fromApiQMSRecord(response.data);

    // List/detail payloads often omit comments; hydrate from comments resource.
    if (record.comments.length === 0) {
      try {
        const comments = await this.getComments(studyId, recordId);
        if (comments.length > 0) {
          return { ...record, comments };
        }
      } catch {
        // Endpoint may be unavailable; keep record as-is.
      }
    }

    return record;
  }

  /**
   * Create new QMS record
   * POST /api/v1/studies/{studyId}/qrm/records
   */
  async createQMSRecord(
    studyId: string,
    data: CreateQMSRecordRequest,
  ): Promise<QMSRecord> {
    const response = await httpCoreService.post<ApiQMSRecord>(
      `/studies/${studyId}/qrm/records`,
      data,
    );
    assertSuccess(response, "Failed to create QMS record");
    return fromApiQMSRecord(response.data);
  }

  /**
   * Update existing QMS record
   * PATCH /api/v1/studies/{studyId}/qrm/records/{recordId}
   */
  async updateQMSRecord(
    studyId: string,
    recordId: string,
    data: UpdateQMSRecordRequest,
  ): Promise<QMSRecord> {
    const response = await httpCoreService.patch<ApiQMSRecord>(
      `/studies/${studyId}/qrm/records/${recordId}`,
      data,
    );
    assertSuccess(response, "Failed to update QMS record");
    return fromApiQMSRecord(response.data);
  }

  /**
   * Delete QMS record
   * DELETE /api/v1/studies/{studyId}/qrm/records/{recordId}
   */
  async deleteQMSRecord(studyId: string, recordId: string): Promise<void> {
    const response = await httpCoreService.delete<null>(
      `/studies/${studyId}/qrm/records/${recordId}`,
    );
    assertSuccess(response, "Failed to delete QMS record");
  }

  /**
   * Close QMS record (status change to Closed)
   * POST /api/v1/studies/{studyId}/qrm/records/{recordId}/close
   */
  async closeRecord(
    studyId: string,
    recordId: string,
    note?: string,
  ): Promise<void> {
    const response = await httpCoreService.post<null>(
      `/studies/${studyId}/qrm/records/${recordId}/close`,
      { note },
    );
    assertSuccess(response, "Failed to close QMS record");
  }

  /**
   * Reopen QMS record (status change back to Open)
   * POST /api/v1/studies/{studyId}/qrm/records/{recordId}/reopen
   */
  async reopenRecord(
    studyId: string,
    recordId: string,
    note?: string,
  ): Promise<void> {
    const response = await httpCoreService.post<null>(
      `/studies/${studyId}/qrm/records/${recordId}/reopen`,
      { note },
    );
    assertSuccess(response, "Failed to reopen QMS record");
  }

  /**
   * Add comment to QMS record
   * POST /api/v1/studies/{studyId}/qrm/records/{recordId}/comments
   */
  async addComment(
    studyId: string,
    recordId: string,
    text: string,
  ): Promise<QMSComment> {
    const response = await httpCoreService.post<unknown>(
      `/studies/${studyId}/qrm/records/${recordId}/comments`,
      { text },
    );
    assertSuccess(response, "Failed to add comment");
    return fromApiQMSComment(response.data);
  }

  /**
   * List comments for a QMS record
   * GET /api/v1/studies/{studyId}/qrm/records/{recordId}/comments
   */
  async getComments(studyId: string, recordId: string): Promise<QMSComment[]> {
    const response = await httpCoreService.get<unknown>(
      `/studies/${studyId}/qrm/records/${recordId}/comments`,
    );
    const ok = response.statusCode >= 200 && response.statusCode < 300;
    if (!ok) {
      throw new Error(
        response.error ?? response.message ?? "Failed to load comments",
      );
    }
    if (response.data === null || response.data === undefined) {
      return [];
    }
    const rows = Array.isArray(response.data)
      ? response.data
      : extractCommentsArray(response.data);
    return rows.map(fromApiQMSComment);
  }

  /**
   * List comments for QMS record
   * GET /api/v1/studies/{studyId}/qrm/records/{recordId}/comments
   */
  async listComments(studyId: string, recordId: string): Promise<QMSComment[]> {
    return this.getComments(studyId, recordId);
  }

  /**
   * Update comment on QMS record
   * PATCH /api/v1/studies/{studyId}/qrm/records/{recordId}/comments/{commentId}
   */
  async updateComment(
    studyId: string,
    recordId: string,
    commentId: string,
    text: string,
  ): Promise<QMSComment> {
    const response = await httpCoreService.patch<unknown>(
      `/studies/${studyId}/qrm/records/${recordId}/comments/${commentId}`,
      { text },
    );
    assertSuccess(response, "Failed to update comment");
    return fromApiQMSComment(response.data);
  }

  /**
   * Delete comment on QMS record
   * DELETE /api/v1/studies/{studyId}/qrm/records/{recordId}/comments/{commentId}
   */
  async deleteComment(
    studyId: string,
    recordId: string,
    commentId: string,
  ): Promise<void> {
    const response = await httpCoreService.delete<Record<string, never>>(
      `/studies/${studyId}/qrm/records/${recordId}/comments/${commentId}`,
    );
    assertSuccess(response, "Failed to delete comment");
  }

  /**
   * Add CAPA to QMS record
   * POST /api/v1/studies/{studyId}/qrm/records/{recordId}/capa
   */
  async addCapa(
    studyId: string,
    recordId: string,
    capa: CAPARecord,
  ): Promise<QMSRecord> {
    const response = await httpCoreService.post<ApiQMSRecord>(
      `/studies/${studyId}/qrm/records/${recordId}/capa`,
      toApiCapaPayload(capa),
    );
    assertSuccess(response, "Failed to add CAPA");
    return fromApiQMSRecord(response.data);
  }

  /**
   * Update CAPA for QMS record
   * PATCH /api/v1/studies/{studyId}/qrm/records/{recordId}/capa
   */
  async updateCapa(
    studyId: string,
    recordId: string,
    capa: CAPARecord,
  ): Promise<QMSRecord> {
    const response = await httpCoreService.patch<ApiQMSRecord>(
      `/studies/${studyId}/qrm/records/${recordId}/capa`,
      toApiCapaPayload(capa),
    );
    assertSuccess(response, "Failed to update CAPA");
    return fromApiQMSRecord(response.data);
  }

  /**
   * Approve CAPA effectiveness (QA/QC only)
   * POST /api/v1/studies/{studyId}/qrm/records/{recordId}/capa/approve
   */
  async approveCapa(
    studyId: string,
    recordId: string,
    payload: {
      effectivenessNotes?: string;
      eSignature: import("@/types/eSignature.type").ESignatureCeremonyPayload;
    },
  ): Promise<QMSRecord> {
    const response = await httpCoreService.post<ApiQMSRecord>(
      `/studies/${studyId}/qrm/records/${recordId}/capa/approve`,
      payload,
    );
    assertSuccess(response, "Failed to approve CAPA");
    return fromApiQMSRecord(response.data);
  }
}
